import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Group, Stack, Tabs, Text} from '@mantine/core';
import React, {useEffect, useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import SkillDetailPanel, {SkillDetailTab} from 'llm/chat-library/skills/SkillDetailPanel';
import {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import SkillsList from 'llm/chat-library/skills/SkillsList';
import UploadSkillModal from 'llm/chat-library/skills/UploadSkillModal';
import useSkills from 'llm/chat-library/skills/useSkills';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  splitContainer: {
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  listStack: {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  headerSection: {
    padding: `${theme.spacing.lg} ${theme.spacing.xxl} 0`,
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
  },
  tab: {
    padding: theme.spacing.md,
    '&[data-active]': {
      borderBottomWidth: 3,
    },
  },
  tabsList: {
    padding: `${theme.spacing.none} ${theme.spacing.xxl}`,
  },
  tabsPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
    padding: `${theme.spacing.md} ${theme.spacing.xxl} ${theme.spacing.lg}`,
  },
  listPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  detailPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
  },
  panelGroup: {
    width: '100%',
    height: '100%',
  },
  resizer: {
    width: '6px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    flexShrink: 0,
    ':hover': {
      backgroundColor: theme.colors.gray[2],
    },
  },
}));

enum SkillsFilterTab {
  MY_SKILLS = 'MY_SKILLS',
  SHARED_SKILLS = 'SHARED_SKILLS',
  ALL_SKILLS = 'ALL_SKILLS',
}

export default function AISettingsSkills({isExpanded}: {isExpanded?: boolean}): JSX.Element {
  const {
    skills,
    isLoading: skillsLoading,
    archiveSkill,
    createNewSkills,
    refresh,
    enableSkill,
    disableSkill,
  } = useSkills();

  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [detailInitialTab, setDetailInitialTab] = useState<SkillDetailTab>(SkillDetailTab.DETAIL);
  const [activeTab, setActiveTab] = useState<SkillsFilterTab>(SkillsFilterTab.MY_SKILLS);
  const {classes} = useStyles();

  const selectedSkill = skills.find((s) => s.id === selectedSkillId) ?? null;
  const mySkills = skills.filter((s) => s.isOwnedByCurrentUser);
  const sharedSkills = skills.filter((s) => !s.isOwnedByCurrentUser);

  useEffect(() => {
    if (!isExpanded) {
      setSelectedSkillId(null);
    }
  }, [isExpanded]);

  function handleSelectSkill(skill: SkillSummary): void {
    if (selectedSkillId === skill.id) {
      setSelectedSkillId(null);
      return;
    }
    setDetailInitialTab(SkillDetailTab.DETAIL);
    setSelectedSkillId(skill.id);
    SidepanelActions.expand();
  }

  function handleManageAccess(skill: SkillSummary): void {
    setDetailInitialTab(SkillDetailTab.ACCESS);
    setSelectedSkillId(skill.id);
    SidepanelActions.expand();
  }

  function handleCloseDetail(): void {
    setSelectedSkillId(null);
  }

  function handleTabChange(tab: string | null): void {
    if (tab == null || !Object.values(SkillsFilterTab).includes(tab as SkillsFilterTab)) {
      return;
    }
    const nextTab = tab as SkillsFilterTab;
    if (nextTab === activeTab) {
      return;
    }
    setActiveTab(nextTab);
    setSelectedSkillId(null);
  }

  const listContent = (
    <Stack spacing="lg" className={classes.listStack}>
      <Stack spacing="sm" className={classes.headerSection}>
        <Group noWrap={true} position="apart" align="center">
          <Text fw="bold" size="xl">
            Skills
          </Text>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            icon="plus"
            onClick={() => {
              legacyRunModal(UploadSkillModal, {
                onRequestSubmit: createNewSkills,
              });
            }}
            size={ButtonSize.SMALL}
            text="Add"
            {...getTestProps({component: 'AISettingsSkills', element: 'uploadSkillButton'})}
          />
        </Group>
        <Text fw="normal" size="sm" c="dimmed">
          Reusable instructions to extend AI agent capabilities.
        </Text>
      </Stack>
      {skillsLoading ? (
        <CenteredSpinner />
      ) : (
        <Tabs
          value={activeTab}
          onTabChange={handleTabChange}
          keepMounted={false}
          className={classes.tabsRoot}
        >
          <Tabs.List className={classes.tabsList}>
            <Tabs.Tab
              value={SkillsFilterTab.ALL_SKILLS}
              className={classes.tab}
              {...getTestProps({component: 'AISettingsSkills', element: 'tab', key: 'AllSkills'})}
            >
              All
            </Tabs.Tab>
            <Tabs.Tab
              value={SkillsFilterTab.MY_SKILLS}
              className={classes.tab}
              {...getTestProps({component: 'AISettingsSkills', element: 'tab', key: 'MySkills'})}
            >
              My skills
            </Tabs.Tab>
            <Tabs.Tab
              value={SkillsFilterTab.SHARED_SKILLS}
              className={classes.tab}
              {...getTestProps({component: 'AISettingsSkills', element: 'tab', key: 'SharedSkills'})}
            >
              Shared with me
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={SkillsFilterTab.ALL_SKILLS} className={classes.tabsPanel}>
            <SkillsList
              skills={skills}
              onArchive={archiveSkill}
              onEnable={enableSkill}
              onDisable={disableSkill}
              selectedSkillId={selectedSkill?.id}
              onSelectSkill={handleSelectSkill}
              onManageAccess={handleManageAccess}
            />
          </Tabs.Panel>
          <Tabs.Panel value={SkillsFilterTab.MY_SKILLS} className={classes.tabsPanel}>
            <SkillsList
              skills={mySkills}
              onArchive={archiveSkill}
              onEnable={enableSkill}
              onDisable={disableSkill}
              selectedSkillId={selectedSkill?.id}
              onSelectSkill={handleSelectSkill}
              onManageAccess={handleManageAccess}
            />
          </Tabs.Panel>
          <Tabs.Panel value={SkillsFilterTab.SHARED_SKILLS} className={classes.tabsPanel}>
            <SkillsList
              skills={sharedSkills}
              onArchive={archiveSkill}
              onEnable={enableSkill}
              onDisable={disableSkill}
              selectedSkillId={selectedSkill?.id}
              onSelectSkill={handleSelectSkill}
              onManageAccess={handleManageAccess}
            />
          </Tabs.Panel>
        </Tabs>
      )}
    </Stack>
  );

  if (!selectedSkill) {
    return listContent;
  }

  return (
    <Box className={classes.splitContainer}>
      <PanelGroup direction="horizontal" className={classes.panelGroup}>
        <Panel defaultSize={40} minSize={25} maxSize={70}>
          <Box className={classes.listPanel}>{listContent}</Box>
        </Panel>
        <PanelResizeHandle className={classes.resizer} />
        <Panel defaultSize={60} minSize={30} maxSize={75}>
          <Box className={classes.detailPanel}>
            <SkillDetailPanel
              skill={selectedSkill}
              onClose={handleCloseDetail}
              onUpdate={refresh}
              onArchive={archiveSkill}
              initialTab={detailInitialTab}
            />
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}
